import React, { useCallback, useRef, useState, ReactNode } from "react";
import { cx, useTranslations, useLoginFlow } from "@jugl-web/utils";
import { PRIVACY_POLICY_URL, TERMS_OF_USE_URL } from "@jugl-web/utils/consts";
import { LoginMethod } from "@web-src/modules/auth/pages/Login/types";
import { EntityInvitationModel, useRestApiProvider } from "@jugl-web/rest-api";
import { JUGL_LOGIN, saveItemToStorage } from "@jugl-web/utils/storage";
import { setJwtToken } from "@web-src/features/auth/utils";
import { PhoneInputValue, Menu } from "@jugl-web/ui-components/cross-platform";
import { PlainButton } from "@jugl-web/ui-components";
import { proceedLogin } from "@web-src/features/auth/authSlice";
import { useAppDispatch } from "@web-src/store";
import { useNavigateToRoleBasedInitialPage } from "@web-src/features/app/hooks/useNavigateToRoleBasedInitialPage";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { useUserAuth } from "@web-src/modules/auth/providers/UserAuthProvider/useUserAuth";
import { useSearchParams } from "react-router-dom";
import { ReactComponent as ThreeDotsIcon } from "@web-src/modules/auth/pages/SignUpFlowLayout/assets/three-dots.svg";
import { ReactComponent as LogoutIcon } from "@web-src/modules/auth/pages/SignUpFlowLayout/assets/menu-logout.svg";
import { ReactComponent as BackArrowIcon } from "@web-src/modules/auth/pages/SignUpFlowLayout/assets/back-arrow.svg";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { CXFlowLayout } from "../CXFlowLayout";
import { CXImageCarousel } from "./CXImageCarousel";
import { CXCredentialsStep } from "./CXCredentialsStep";
import { CXOtpStep } from "./CXOtpStep";
import { CXProfileForm } from "./CXProfileForm";
import { CXWorkspaceForm } from "./CXWorkspaceForm";
import { CXInvitationsList } from "./CXInvitationsList";

export type CXOnboardingStep =
  | "credentials"
  | "otp"
  | "profile"
  | "invitations"
  | "workspace";

export interface CXOnboardingFlowProps {
  initialStep: CXOnboardingStep;
  showCarousel?: boolean;
  invitations?: EntityInvitationModel[];
}

export const CXOnboardingFlow: React.FC<CXOnboardingFlowProps> = ({
  initialStep,
  showCarousel,
  invitations: preloadedInvitations,
}) => {
  const { t } = useTranslations();
  const dispatch = useAppDispatch();
  const { setLoginFlow } = useLoginFlow();
  const { entitiesApi, usersApi } = useRestApiProvider();
  const { navigateToPage } = useNavigation();
  const navigateToRoleBasedInitialPage = useNavigateToRoleBasedInitialPage();
  const { logout } = useUserAuth();
  const [searchParams] = useSearchParams();
  const flow = searchParams.get("flow");
  const showBackButton = flow === "org-create";

  const [fetchInvites] = entitiesApi.useLazyGetEntityInvitesQuery();
  const [fetchEntities] =
    entitiesApi.useLazy_DEPRECATED_getAllOrgEntitiesListQuery();
  const [step, setStep] = useState<CXOnboardingStep>(initialStep);
  const [invitations, setInvitations] = useState<EntityInvitationModel[]>(
    preloadedInvitations || []
  );
  const { data: profile } = usersApi.useGetUserProfileQuery(
    ["credentials", "otp"].includes(step) ? skipToken : undefined
  );
  const [loginMethod, setLoginMethod] = useState<LoginMethod>(
    LoginMethod.email
  );
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState<PhoneInputValue>({
    code: "",
    iso: "",
    phone: "",
    isValid: false,
  });
  const [otpRequestId, setOtpRequestId] = useState("");

  const credentialsRef = useRef<{
    token: string;
    refreshToken: string;
    login: string;
  } | null>(null);

  // --- OTP verified ---
  const handleOtpVerified = useCallback(
    async (credentials: {
      token: string;
      refreshToken: string;
      login: string;
    }) => {
      saveItemToStorage(JUGL_LOGIN, credentials.login);
      setJwtToken(credentials.token, credentials.refreshToken);
      credentialsRef.current = credentials;

      // Fetch invitations and entities to decide next step
      const [invitesResult, entitiesResult] = await Promise.all([
        fetchInvites(),
        fetchEntities(),
      ]);

      const fetchedInvites = invitesResult.data || [];
      const fetchedEntities = entitiesResult.data || [];

      if (fetchedEntities.length > 0) {
        // Has entities already — login and navigate
        await dispatch(proceedLogin(credentialsRef.current));
        setLoginFlow(null);
        navigateToRoleBasedInitialPage(fetchedEntities[0]);
      } else if (fetchedInvites.length > 0) {
        setInvitations(fetchedInvites);
        if (!profile) {
          setStep("profile");
        } else {
          setStep("invitations");
        }
      } else {
        setStep("workspace");
      }
    },
    [
      dispatch,
      fetchEntities,
      fetchInvites,
      navigateToRoleBasedInitialPage,
      profile,
      setLoginFlow,
    ]
  );

  // --- Back to credentials from OTP ---
  const handleOtpBack = useCallback(() => {
    setStep("credentials");
  }, []);

  // --- Resend OTP (re-request) ---
  const handleResendOtp = useCallback((e?: React.FormEvent) => {
    e?.preventDefault();
    // The credentials step handles this internally,
    // but since we're in OTP step, we need to re-request.
    // We reuse the same requestOtp logic by going through the mutation directly.
    // For simplicity, this triggers the parent's resend which is effectively
    // going back to credentials and re-submitting — but the OTPVerificationForm
    // handles the resend timer internally.
  }, []);

  // --- Profile completed ---
  const handleProfileSuccess = useCallback(() => {
    if (invitations.length > 0) {
      setStep("invitations");
    } else {
      setStep("workspace");
    }
  }, [invitations.length]);

  // --- Workspace created ---
  const handleWorkspaceSuccess = useCallback(
    async (entityId: string) => {
      if (credentialsRef.current) {
        await dispatch(proceedLogin(credentialsRef.current));
      }
      setLoginFlow(null);
      navigateToPage("cxAIAgents", undefined, { entityId });
    },
    [dispatch, navigateToPage, setLoginFlow]
  );

  // --- Invitation accepted ---
  const handleInvitationAccepted = useCallback(
    async (entityId: string) => {
      if (credentialsRef.current) {
        await dispatch(proceedLogin(credentialsRef.current));
      }
      setLoginFlow(null);
      navigateToPage("cxAIAgents", undefined, { entityId });
    },
    [dispatch, navigateToPage, setLoginFlow]
  );

  // --- Create workspace from invitations step ---
  const handleCreateWorkspaceFromInvitations = useCallback(() => {
    setStep("workspace");
  }, []);

  const showProfileFields = !profile;
  const initialEmailForWorkspace =
    loginMethod === LoginMethod.email ? email : undefined;

  return (
    <CXFlowLayout>
      {showCarousel && <CXImageCarousel />}
      {showBackButton && (
        <PlainButton
          className="absolute top-[36px] left-[36px] z-10 flex h-[40px] w-[40px] items-center justify-center rounded-md bg-grey-200 transition-colors duration-300 hover:brightness-95"
          onClick={() => navigateToPage("authNewWorkspace", { flow: "inapp" })}
        >
          <BackArrowIcon />
        </PlainButton>
      )}
      {step !== "credentials" && (
        <Menu
          placement="bottom-end"
          className="py-2"
          sections={[
            [
              {
                id: "logout",
                label: t({
                  id: "signup-Layout.logout",
                  defaultMessage: "Log out",
                }),
                icon: <LogoutIcon />,
                labelClassName: "text-base text-dark tracking-0 px-0",
                onSelect: (_: unknown, close: () => void) => {
                  close();
                  logout(true, () => setStep("credentials"));
                },
              },
            ],
          ]}
          renderTrigger={({ Trigger, triggerRef, isOpen }) => (
            <Trigger
              as={PlainButton}
              ref={triggerRef}
              className={cx(
                "absolute top-[36px] right-[36px] z-10 flex h-[40px] w-[40px] items-center justify-center rounded-md transition-colors duration-300 hover:brightness-95",
                { "bg-grey-200": isOpen }
              )}
            >
              <ThreeDotsIcon />
            </Trigger>
          )}
        />
      )}
      <div className="jugl__custom-scrollbar flex h-full w-full flex-col items-center justify-between gap-8 overflow-y-auto px-10 py-4">
        {step === "credentials" && (
          <CXCredentialsStep
            email={email}
            onEmailChange={setEmail}
            phone={phone}
            onPhoneChange={setPhone}
            loginMethod={loginMethod}
            onLoginMethodChange={setLoginMethod}
            onSuccess={(id) => {
              setOtpRequestId(id);
              setStep("otp");
            }}
          />
        )}
        {step === "otp" && (
          <CXOtpStep
            otpRequestId={otpRequestId}
            loginMethod={loginMethod}
            email={email}
            phone={phone}
            onVerified={handleOtpVerified}
            onBack={handleOtpBack}
            onResendOtp={handleResendOtp}
          />
        )}
        {step === "profile" && (
          <CXProfileForm onSuccess={handleProfileSuccess} />
        )}
        {step === "invitations" && (
          <CXInvitationsList
            invitations={invitations}
            onAccepted={handleInvitationAccepted}
            onCreateWorkspace={handleCreateWorkspaceFromInvitations}
          />
        )}
        {step === "workspace" && (
          <CXWorkspaceForm
            onSuccess={handleWorkspaceSuccess}
            showProfileFields={showProfileFields}
            initialEmail={initialEmailForWorkspace}
          />
        )}
        <span className="font-secondary text-sm leading-[160%] text-dark-500">
          {t(
            {
              id: "cx-ai-agents-page.terms-and-privacy",
              defaultMessage: `<termsLink>Terms and Conditions</termsLink> and <privacyLink>Privacy Policy</privacyLink>`,
            },
            {
              termsLink: (text: ReactNode) => (
                <a
                  className="text-primary-600 underline"
                  href={TERMS_OF_USE_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  {text}
                </a>
              ),
              privacyLink: (text: ReactNode) => (
                <a
                  className="text-primary-600 underline"
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  {text}
                </a>
              ),
            }
          )}
        </span>
      </div>
    </CXFlowLayout>
  );
};
